// useFiltroPersistido.ts — un useState que recuerda el filtro del admin.
//
// Uso: se cambia `useState` por `useFiltroPersistido` y se le da una clave.
//
//   const [busqueda, setBusqueda] = useFiltroPersistido("arriendos.busqueda", "");
//   const [rango, setRango] = useRangoPersistido("arriendos.rango", null);
//
// La clave es "<pagina>.<filtro>" y es global: dos páginas con la misma clave
// comparten el filtro. Si un componente lo usan varias páginas (ArriendosSection
// vive en /arriendos y en /finanzas-empresa), la clave se pasa por prop.
//
// ── Decisiones, con su porqué ────────────────────────────────────────────
// · SOLO admin y superadmin. Es lo que pidió el dueño y además evita cambiarle
//   la vista al cliente o a la aseadora sin que nadie lo haya pedido.
// · Se guarda RECIÉN cuando el usuario toca el filtro. Si guardáramos el valor
//   inicial, congelaríamos defaults que son relativos a hoy: /lavanderia abre
//   en "el mes actual" y, guardado el 30 de septiembre, el 2 de octubre
//   restauraría septiembre. Mismo criterio que el guard de finanzasClientes.tsx
//   (la preferencia solo se escribe desde un clic real).
// · La identidad llega tarde. AuthContext pinta primero el `user` cacheado y
//   recién después lo revalida contra /auth/me. Por eso leemos con el id que
//   haya al montar y, si el id definitivo resulta ser otro, volvemos a leer —
//   salvo que el usuario ya haya tocado el filtro, porque pisarle lo que
//   acaba de escribir es peor que no restaurar nada.
// · Se lee con useContext y NO con useAuth(): useAuth lanza fuera del provider
//   y las páginas se testean sin AuthProvider. Sin contexto, el hook se
//   comporta exactamente como useState.
// · Enlaces directos (?sector=, ?mes=, ?bookingId=) mandan sobre lo guardado:
//   la página pasa `restaurar: false` cuando la URL trae parámetros. Ver
//   docs/filtros-persistentes.md.
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { AuthContext } from "../context/AuthContext";
import { isAdminRole } from "../utils/roles.utils";
import {
  CADUCIDAD_FECHAS_DIAS,
  OpcionesLectura,
  RangoDayjs,
  deserializarRango,
  guardarFiltro,
  leerFiltro,
  serializarRango,
} from "./filtrosPersistidos";

/**
 * Espera antes de escribir. Un buscador escribe en cada tecla; sin esto serían
 * decenas de escrituras a localStorage por palabra.
 */
export const RETARDO_GUARDADO_MS = 400;

export type OpcionesFiltroPersistido<T> = OpcionesLectura<T> & {
  /** Cómo se guarda el valor (ej. Dayjs → "YYYY-MM-DD"). */
  serializar?: (valor: T) => unknown;
  /**
   * `false` para ignorar lo guardado en este montaje, sin dejar de guardar los
   * cambios que venga. Se usa cuando la URL trae un enlace directo.
   *
   * Se lee UNA sola vez, al montar: las páginas lo derivan de la query string
   * y el efecto del enlace directo la limpia (`setSearchParams({})`), así que
   * recalcularlo volvería a abrir la restauración encima del valor que acaba
   * de poner el enlace.
   */
  restaurar?: boolean;
};

/**
 * `useState` que recuerda su valor entre recargas, cambios de página y
 * sesiones, por usuario y solo para admin/superadmin.
 *
 * Devuelve un tercer elemento, `setSinRecordar`, para lo que NO es una
 * elección del admin: el valor que impone un enlace directo o una precarga
 * automática. Cambia la pantalla sin guardar nada, para que abrir una alerta
 * de diciembre no deje diciembre como el período preferido de esa persona.
 */
export function useFiltroPersistido<T>(
  clave: string,
  inicial: T | (() => T),
  opciones: OpcionesFiltroPersistido<T> = {},
): [T, Dispatch<SetStateAction<T>>, Dispatch<SetStateAction<T>>] {
  const auth = useContext(AuthContext);
  const idUsuario = auth?.user?.id;
  const activo = isAdminRole(auth?.user?.rol) && idUsuario != null;
  const [restaurarPermitido] = useState(opciones.restaurar !== false);
  const puedeRestaurar = activo && restaurarPermitido;

  // Las opciones traen funciones nuevas en cada render; por referencia, para
  // que los efectos no dependan de ellas.
  const opcionesRef = useRef(opciones);
  opcionesRef.current = opciones;

  const [valor, escribirValor] = useState<T>(() => {
    if (puedeRestaurar) {
      const guardado = leerFiltro<T>(idUsuario as number, clave, opciones);
      if (guardado !== undefined) return guardado as T;
    }
    return typeof inicial === "function" ? (inicial as () => T)() : inicial;
  });

  /** ¿Alguien ya cambió este filtro en este montaje? Antes de eso no se guarda. */
  const tocado = useRef(false);
  /** Con qué id se leyó el valor actual, para detectar que la sesión era otra. */
  const idLeido = useRef<number | undefined>(puedeRestaurar ? idUsuario : undefined);

  /** El último valor puesto por `setSinRecordar`, para no guardarlo. */
  const sinRecordar = useRef<{ valor: T } | null>(null);

  const setValor = useCallback<Dispatch<SetStateAction<T>>>((accion) => {
    tocado.current = true;
    escribirValor(accion);
  }, []);

  const setSinRecordar = useCallback<Dispatch<SetStateAction<T>>>((accion) => {
    escribirValor((previo) => {
      const siguiente =
        typeof accion === "function" ? (accion as (p: T) => T)(previo) : accion;
      sinRecordar.current = { valor: siguiente };
      return siguiente;
    });
  }, []);

  // La identidad definitiva llegó (o entró otra persona en esta pestaña).
  useEffect(() => {
    if (!puedeRestaurar || idLeido.current === idUsuario) return;
    idLeido.current = idUsuario;
    if (tocado.current) return;
    const guardado = leerFiltro<T>(idUsuario as number, clave, opcionesRef.current);
    if (guardado !== undefined) escribirValor(guardado as T);
  }, [puedeRestaurar, idUsuario, clave]);

  useEffect(() => {
    // Lo que puso `setSinRecordar` se muestra pero no se guarda, aunque el
    // admin ya hubiera tocado este filtro antes en la misma visita.
    if (sinRecordar.current && Object.is(sinRecordar.current.valor, valor)) {
      sinRecordar.current = null;
      return;
    }
    if (!activo || !tocado.current) return;
    const id = setTimeout(() => {
      guardarFiltro(idUsuario as number, clave, valor, opcionesRef.current.serializar);
    }, RETARDO_GUARDADO_MS);
    return () => clearTimeout(id);
  }, [activo, idUsuario, clave, valor]);

  return [valor, setValor, setSinRecordar];
}

/**
 * Lo mismo para el par de fechas de un RangePicker: guarda texto, devuelve
 * Dayjs y caduca a los 7 días para no dejar al admin mirando un rango viejo.
 */
export function useRangoPersistido(
  clave: string,
  inicial: RangoDayjs | (() => RangoDayjs),
  opciones: Omit<
    OpcionesFiltroPersistido<RangoDayjs>,
    "serializar" | "deserializar"
  > = {},
): [
  RangoDayjs,
  Dispatch<SetStateAction<RangoDayjs>>,
  Dispatch<SetStateAction<RangoDayjs>>,
] {
  return useFiltroPersistido<RangoDayjs>(clave, inicial, {
    caducaEnDias: CADUCIDAD_FECHAS_DIAS,
    ...opciones,
    serializar: serializarRango,
    deserializar: deserializarRango,
  });
}
